// src/components/layout/app-shell.jsx
import React, { useState } from "react";
import { Menu, X, Home, ShoppingBag, Sparkles, ChefHat, Palette, Calendar, Package, Gift, ShoppingCart, User, LogIn } from "lucide-react";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import ThemeSelector from "./theme-selector";
import { useAuth } from "../../contexts/AuthContext";
import { useCart } from "../../contexts/CartContext";
import { LogoSidebar, LogoCrest } from "../Logo";

const navItems = [
  { href: "home", label: "Home", icon: Home },
  { href: "products", label: "Products", icon: ShoppingBag },
  { href: "customization", label: "Customization", icon: Sparkles },
  { href: "culinary-partners", label: "Culinary", icon: ChefHat },
  { href: "mood-board", label: "Mood Board AI", icon: Palette },
  { href: "booking", label: "Booking", icon: Calendar },
  { href: "subscription", label: "Subscription", icon: Package },
  { href: "registry", label: "Registry", icon: Gift },
];

export default function AppShell({ children, currentRoute, navigate }) {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const { currentUser } = useAuth();
  const { getCartCount } = useCart();
  const cartCount = getCartCount();

  const handleNavigate = (href) => {
    navigate(href);
    setSidebarOpen(false);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-secondary/60 to-accent/30 dark:from-background dark:via-secondary/40 dark:to-accent/20">
      {/* Mobile Header */}
      <header className="lg:hidden sticky top-0 z-50 backdrop-blur-md bg-background/70 border-b border-border">
        <div className="flex items-center justify-between p-4">
          <button
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className="p-2 hover:bg-secondary/70 rounded-lg transition-colors"
          >
            {sidebarOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
          <div className="flex items-center gap-2" onClick={() => navigate('home')}>
            <LogoCrest className="h-8 w-auto" />
            <span className="text-lg font-headline font-bold text-gold-gradient" style={{
              background: 'linear-gradient(135deg, #C9A962 0%, #E5D4A1 50%, #C9A962 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent'
            }}>
              Aurum & Ivy
            </span>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => navigate('cart')}
              className="relative p-2 hover:bg-secondary/70 rounded-lg transition-colors"
            >
              <ShoppingCart className="h-6 w-6" />
              {cartCount > 0 && (
                <Badge className="absolute -top-1 -right-1 h-5 w-5 flex items-center justify-center p-0 bg-primary text-primary-foreground text-xs">
                  {cartCount}
                </Badge>
              )}
            </button>
          </div>
        </div>
      </header>

      <div className="flex">
        {/* Sidebar */}
        <aside
          className={`fixed lg:sticky top-0 left-0 h-screen w-64 backdrop-blur-xl bg-background/80 border-r border-border z-40 transition-transform duration-300 ${
            sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'
          }`}
        >
          <div className="flex flex-col h-full">
            {/* Logo */}
            <div className="p-6 border-b border-border">
              <LogoSidebar onClick={() => handleNavigate('home')} />
            </div>

            {/* Navigation */}
            <nav className="flex-1 overflow-y-auto p-4 space-y-2">
              {navItems.map((item) => {
                const Icon = item.icon;
                const isActive = currentRoute === item.href;
                return (
                  <button
                    key={item.href}
                    onClick={() => handleNavigate(item.href)}
                    className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-all duration-200 ${
                      isActive
                        ? 'bg-primary text-primary-foreground shadow-lg shadow-primary/30'
                        : 'hover:bg-secondary/70 text-muted-foreground'
                    }`}
                  >
                    <Icon className="h-5 w-5" />
                    <span className="font-medium">{item.label}</span>
                  </button>
                );
              })}

              {/* Cart & User Actions */}
              <div className="pt-4 mt-4 border-t border-border space-y-2">
                <button
                  onClick={() => handleNavigate('cart')}
                  className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-all duration-200 ${
                    currentRoute === 'cart'
                      ? 'bg-primary text-primary-foreground shadow-lg shadow-primary/30'
                      : 'hover:bg-secondary/70 text-muted-foreground'
                  }`}
                >
                  <ShoppingCart className="h-5 w-5" />
                  <span className="font-medium">Cart</span>
                  {cartCount > 0 && (
                    <Badge className="ml-auto bg-primary text-primary-foreground">
                      {cartCount}
                    </Badge>
                  )}
                </button>

                {currentUser ? (
                  <button
                    onClick={() => handleNavigate('account')}
                    className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-all duration-200 ${
                      currentRoute === 'account'
                        ? 'bg-primary text-primary-foreground shadow-lg shadow-primary/30'
                        : 'hover:bg-secondary/70 text-muted-foreground'
                    }`}
                  >
                    <User className="h-5 w-5" />
                    <span className="font-medium">Account</span>
                  </button>
                ) : (
                  <button
                    onClick={() => handleNavigate('login')}
                    className="w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-all duration-200 hover:bg-secondary/70 text-muted-foreground"
                  >
                    <LogIn className="h-5 w-5" />
                    <span className="font-medium">Login</span>
                  </button>
                )}
              </div>
            </nav>

            {/* Footer with Theme Selector */}
            <div className="p-4 border-t border-border">
              {currentUser && (
                <div className="mb-3 px-2 text-sm text-muted-foreground truncate">
                  {currentUser.displayName || currentUser.email}
                </div>
              )}
              <ThemeSelector />
            </div>
          </div>
        </aside>

        {/* Main Content */}
        <main className="flex-1 overflow-y-auto">
          <div className="min-h-screen">
            {children}
          </div>
        </main>
      </div>

      {/* Overlay for mobile */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-30 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}
    </div>
  );
}
